import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { AddRounded as AddIcon, EditOutlined as EditIcon } from '@mui/icons-material';
import { segmentedSx } from '../monthly/MultiSelectFilter';
import { GREEN, GREEN_DARK, GREEN_SOFT, INK, LINE, MUTED } from '../home/homeTheme';
import { fetchLookupValues, LOOKUP_FIELDS, MONTHLY_RATE_VALUE, saveLookupValue, toggleLookupActive } from '../../utils/contractsService';

const EMPTY_LOOKUP_FORM = { label: '', sort_order: 0 };

/**
 * Valori consentiti per Cluster, Type e Renewal Status (ex "Manage Contract Values"): stessa logica della pagina classica.
 * Alla chiusura la pagina ricarica i valori attivi.
 */
export default function ContractValuesDialog({ open, acl, onClose }) {
  const [tab, setTab] = useState(LOOKUP_FIELDS[0].field_key);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_LOOKUP_FORM);
  const [saving, setSaving] = useState(false);

  const load = async (fieldKey) => {
    setLoading(true);
    setError(null);
    try {
      setItems(await fetchLookupValues(fieldKey));
    } catch (e) {
      setError(e.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setFormOpen(false);
    setEditing(null);
    load(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);
  useEffect(() => {
    if (open) setTab(LOOKUP_FIELDS[0].field_key);
  }, [open]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_LOOKUP_FORM, sort_order: (items.length + 1) * 10 });
    setFormOpen(true);
    setError(null);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ label: item.label || '', sort_order: item.sort_order ?? 0 });
    setFormOpen(true);
    setError(null);
  };
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveLookupValue({ editing, fieldKey: tab, form, acl });
      setFormOpen(false);
      setEditing(null);
      await load(tab);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };
  const toggle = async (item) => {
    setError(null);
    try {
      await toggleLookupActive(item, acl);
      await load(tab);
    } catch (e) {
      setError(e.message);
    }
  };
  const label = LOOKUP_FIELDS.find((f) => f.field_key === tab)?.label;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>Contract values</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: MUTED, mb: 2 }}>
          Maintain allowed values for Cluster, Type and Renewal Status. Type is also used as Service Type on projects.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <ToggleButtonGroup exclusive size="small" value={tab} onChange={(e, v) => v && setTab(v)} sx={segmentedSx}>
            {LOOKUP_FIELDS.map((f) => (
              <ToggleButton key={f.field_key} value={f.field_key}>
                {f.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="contained" color="success" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', bgcolor: GREEN }}>
            Add value
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {formOpen && (
          <Box sx={{ p: 1.75, mb: 1.5, borderRadius: 2.5, bgcolor: 'var(--rt-subtle)', border: `1px solid ${LINE}`, boxShadow: `inset 3px 0 0 ${GREEN}` }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.25 }}>
              {editing ? 'Edit value' : `New ${label} value`}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 120px' }, gap: 1.5 }}>
              <TextField
                label="Label (display)"
                size="small"
                value={form.label}
                onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
                required
                autoFocus
                helperText="Shown in dropdowns. Renaming does not change existing contract records."
                sx={{ bgcolor: 'var(--rt-paper)' }}
              />
              <TextField label="Sort order" size="small" type="number" value={form.sort_order} onChange={(e) => setForm((p) => ({ ...p, sort_order: e.target.value }))} sx={{ bgcolor: 'var(--rt-paper)' }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button
                color="inherit"
                onClick={() => {
                  setFormOpen(false);
                  setEditing(null);
                }}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button variant="contained" color="success" onClick={save} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', bgcolor: GREEN, minWidth: 90 }}>
                {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
              </Button>
            </Box>
          </Box>
        )}

        {loading ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} color="success" />
          </Box>
        ) : !items.length ? (
          <Typography sx={{ fontSize: 13.5, color: MUTED, py: 2 }}>No values yet.</Typography>
        ) : (
          <Box sx={{ border: `1px solid ${LINE}`, borderRadius: 2.5, overflow: 'hidden' }}>
            {items.map((item, i) => (
              <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1, borderTop: i ? `1px solid ${LINE}` : 'none', bgcolor: item.is_active ? 'transparent' : 'var(--rt-subtle)' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: item.is_active ? INK : MUTED }}>{item.label}</Typography>
                  <Typography sx={{ fontSize: 12, color: MUTED }}>
                    Sort order {item.sort_order}
                    {item.value === MONTHLY_RATE_VALUE ? ' · drives Current Monthly Fee on Revenues' : ''}
                  </Typography>
                </Box>
                {item.value === MONTHLY_RATE_VALUE && (
                  <Box component="span" sx={{ px: 0.9, py: 0.2, borderRadius: 999, fontSize: 11.5, fontWeight: 700, bgcolor: 'var(--rt-forecast-soft)', color: 'var(--rt-forecast-ink)' }}>
                    Monthly Rate
                  </Box>
                )}
                <Box component="span" sx={{ px: 0.9, py: 0.2, borderRadius: 999, fontSize: 11.5, fontWeight: 700, bgcolor: item.is_active ? GREEN_SOFT : 'var(--rt-track)', color: item.is_active ? GREEN_DARK : MUTED }}>
                  {item.is_active ? 'Active' : 'Inactive'}
                </Box>
                <Tooltip title="Edit label / sort">
                  <IconButton size="small" onClick={() => openEdit(item)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={item.is_active ? 'Deactivate' : 'Activate'}>
                  <Switch size="small" color="success" checked={Boolean(item.is_active)} onChange={() => toggle(item)} />
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
